import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PortfolioPreview = ({ currentTheme }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [hoveredItem, setHoveredItem] = useState(null);

  const portfolioItems = [
    {
      id: 1,
      title: 'Romantic Garden Wedding',
      category: 'events',
      client: 'Sarah & Michael',
      image: 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=600&h=400&fit=crop',
      duration: '3:45',
      views: '12.5K',
      type: 'Wedding Film'
    },
    {
      id: 2,
      title: 'Modern Downtown Penthouse',
      category: 'realestate',
      client: 'Luxury Properties Inc.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop',
      duration: '2:30',
      views: '8.2K',
      type: 'Property Tour'
    },
    {
      id: 3,
      title: 'Tech Startup Brand Launch',
      category: 'marketing',
      client: 'InnovateTech',
      image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=600&h=400&fit=crop',
      duration: '1:20',
      views: '25.7K',
      type: 'Brand Campaign'
    },
    {
      id: 4,
      title: 'Future City Concept',
      category: 'design',
      client: 'Urban Futures Lab',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=600&h=400&fit=crop',
      duration: '4:15',
      views: '15.3K',
      type: 'Concept Film'
    },
    {
      id: 5,
      title: 'Intimate Beach Ceremony',
      category: 'events',
      client: 'Emma & David',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop',
      duration: '2:55',
      views: '9.8K',
      type: 'Wedding Film'
    },
    {
      id: 6,
      title: 'Luxury Resort Marketing',
      category: 'marketing',
      client: 'Paradise Resorts',
      image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=600&h=400&fit=crop',
      duration: '1:45',
      views: '18.9K',
      type: 'Commercial'
    }
  ];

  const filters = [
    { id: 'all', label: 'All Work', count: portfolioItems?.length },
    { id: 'events', label: 'Events', count: portfolioItems?.filter(item => item?.category === 'events')?.length },
    { id: 'realestate', label: 'Real Estate', count: portfolioItems?.filter(item => item?.category === 'realestate')?.length },
    { id: 'marketing', label: 'Marketing', count: portfolioItems?.filter(item => item?.category === 'marketing')?.length },
    { id: 'design', label: 'Design Fiction', count: portfolioItems?.filter(item => item?.category === 'design')?.length }
  ];

  const filteredItems = activeFilter === 'all' 
    ? portfolioItems 
    : portfolioItems?.filter(item => item?.category === activeFilter);

  const getCategoryColor = (category) => {
    const colors = {
      events: 'text-rose-500 bg-rose-50',
      realestate: 'text-blue-600 bg-blue-50',
      marketing: 'text-orange-600 bg-orange-50',
      design: 'text-purple-600 bg-purple-50'
    };
    return colors?.[category] || 'text-gray-600 bg-gray-50';
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-fluid-4xl font-display font-bold text-foreground mb-6">
            Recent
            <span className="text-accent"> Masterpieces</span>
          </h2>
          <p className="text-fluid-lg text-muted-foreground leading-relaxed">
            Explore our latest visual storytelling projects across diverse industries, 
            each crafted with precision and creative excellence.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {filters?.map((filter) => (
            <button
              key={filter?.id}
              onClick={() => setActiveFilter(filter?.id)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                activeFilter === filter?.id
                  ? 'bg-accent text-accent-foreground shadow-md'
                  : 'bg-background text-muted-foreground hover:bg-accent/10 hover:text-accent'
              }`}
            >
              {filter?.label}
              <span className="ml-2 text-xs opacity-70">({filter?.count})</span>
            </button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {filteredItems?.map((item) => (
            <div
              key={item?.id}
              className="group relative bg-card rounded-2xl overflow-hidden border border-border hover-lift transition-all duration-500"
              onMouseEnter={() => setHoveredItem(item?.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {/* Image Container */}
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={item?.image}
                  alt={item?.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-300">
                    <Icon name="Play" size={24} className="text-white ml-1" />
                  </div>
                </div>

                {/* Duration Badge */}
                <div className="absolute top-4 right-4 bg-black/70 text-white text-xs font-medium px-2 py-1 rounded">
                  {item?.duration}
                </div>

                {/* Category Badge */}
                <div className={`absolute top-4 left-4 text-xs font-medium px-3 py-1 rounded-full ${getCategoryColor(item?.category)}`}>
                  {item?.type}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-foreground group-hover:text-accent transition-colors duration-300">
                    {item?.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {item?.client}
                  </p>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Eye" size={14} />
                    <span>{item?.views} views</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Heart" size={14} />
                    <span>Featured</span>
                  </div>
                </div>
              </div>

              {/* Hover Border Effect */}
              <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-accent/30 transition-all duration-300 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center space-y-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              variant="default"
              size="lg"
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
              iconName="Grid"
              iconPosition="left"
            >
              <Link to="/dynamic-portfolio-ecosystem" className="flex items-center space-x-2">
                <span>View Full Portfolio</span>
              </Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
            >
              <Link to="/contact-scheduling-hub" className="flex items-center space-x-2">
                <span>Discuss Your Project</span>
              </Link>
            </Button>
          </div>
          
          <p className="text-muted-foreground text-sm">
            Over 500+ successful projects delivered across all industries
          </p>
        </div>
      </div>
    </section>
  );
};

export default PortfolioPreview;